import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiError, apiFetch } from 'API/apiClient';
import type { IAuthUser, IError } from 'types/Common';
import type { IUser } from 'types/IUser';
import type { RootState } from 'store/store';

interface AuthResponse {
  message: string;
  user: IAuthUser;
}

interface MeResponse {
  user: IAuthUser;
}

async function fetchUserDetails(userId: number): Promise<{ permissions: number[]; lotteryId?: number }> {
  try {
    const user = await apiFetch<IUser>(`/users/${userId}`);
    return {
      permissions: (user.userAccountPermissions ?? []).map((p) => Number(p.permissionsId)),
      lotteryId: user.lotteryId ? Number(user.lotteryId) : undefined,
    };
  } catch {
    return { permissions: [] };
  }
}

export const setLoginError = createAction<IError | null>('auth/setLoginError');
export const updateLoginForm = createAction<{ username: string; password: string }>('auth/updateLoginForm');

export const currentUser = createAsyncThunk<MeResponse, undefined, { rejectValue: ApiError; state: RootState }>(
  'authentication/current-user',
  async (_, { rejectWithValue }) => {
    try {
      const me = await apiFetch<{ user: { id: number; username: string; email: string } }>('/auth/me', { method: 'POST' });
      const details = await fetchUserDetails(me.user.id);
      return { user: { ...me.user, ...details } };
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

export const login = createAsyncThunk<AuthResponse, { username: string; password: string }, { rejectValue: ApiError; state: RootState }>(
  'login/post',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const auth = await apiFetch<{ message: string; user: { id: number; username: string; email: string } }>('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const details = await fetchUserDetails(auth.user.id);
      return { ...auth, user: { ...auth.user, ...details } };
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

export const logout = createAsyncThunk<undefined, undefined, { rejectValue: ApiError; state: RootState }>('logout/post', async (_, { rejectWithValue }) => {
  try {
    await apiFetch<undefined>('/auth/logout', { method: 'POST' }, rejectWithValue);
    return;
  } catch (e) {
    return rejectWithValue(e as ApiError);
  }
});
